/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import type { Geometry, MultiPolygon, Polygon } from 'geojson';
import React from 'react';
import type { Position, Source } from '@opengeoweb/shared';
import {
  ToolContainerDraggable,
  renderCounter,
  useWheelStopPropagation,
} from '@opengeoweb/shared';
import { ChevronDown } from '@opengeoweb/theme';
import InfoDialogBaseInfo from './InfoDialogBaseInfo';
import InfoDialogContacts from './InfoDialogContacts';
import InfoDialogGeographicalCoverage from './InfoDialogGeographicalCoverage';
import InfoDialogKeywords from './InfoDialogKeywords';
import InfoDialogLinks from './InfoDialogLinks';
import InfoDialogTime from './InfoDialogTime';
import InfoDialogWMSMapConnect from './InfoDialogWMSMapConnect';
import type { DiscoveryMetadataFeature } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

const isPolygonalGeometry = (
  geometry: Geometry | null,
): geometry is Polygon | MultiPolygon =>
  geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';

export interface DataExplorerInfoProps {
  onClose: () => void;
  isOpen: boolean;
  onMouseDown?: () => void;
  order?: number;
  source?: Source;
  startPosition?: Position;
  featureInfo?: DiscoveryMetadataFeature | null;
  datasetLink: string;
}

const DataExplorerInfo: React.FC<DataExplorerInfoProps> = ({
  onClose,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  startPosition = { top: 85, left: 50 },
  featureInfo,
  datasetLink,
}: DataExplorerInfoProps) => {
  const minSize = { width: 100, height: 126 };
  renderCounter.count(DataExplorerInfo.name);
  const { t } = useDataExplorerTranslation();

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  const placeHolderLoading = 'DISCOVERY CATALOGUE INFO LOADING...';

  const isWmsFormat = (formats: { name?: string }[]): boolean => {
    return formats.some((format) =>
      format?.name?.toLowerCase().includes('wms'),
    );
  };

  const usefulLinks = featureInfo?.links?.filter((link) => !!link?.href) ?? [];

  return (
    <ToolContainerDraggable
      className="dataExplorerInfo"
      data-testid="dataExplorerInfoWindow"
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={{ width: 720, height: 600 }}
      title={
        featureInfo?.properties.title ||
        featureInfo?.properties.identifier ||
        featureInfo?.id
      }
    >
      {featureInfo ? (
        <Box
          data-testid="dataExplorerInfoBox"
          onKeyDown={(event): void => {
            event.stopPropagation();
          }}
          ref={stopWheelEventPropagationRef}
          style={{ paddingBottom: 20 }}
        >
          <InfoDialogBaseInfo
            datasetLink={datasetLink}
            id={featureInfo.id}
            properties={featureInfo.properties}
          />

          {!!featureInfo.properties.formats &&
          isWmsFormat(featureInfo.properties.formats) &&
          isPolygonalGeometry(featureInfo.geometry) &&
          featureInfo.links ? (
            <InfoDialogWMSMapConnect
              geometry={featureInfo.geometry}
              links={featureInfo.links}
            />
          ) : null}

          {!!featureInfo.geometry && (
            <Accordion
              defaultExpanded={true}
              disableGutters
              onClick={(e) => e.stopPropagation()}
              slotProps={{ transition: { unmountOnExit: true } }}
              style={{ marginTop: 15 }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                style={{ paddingTop: 5, paddingBottom: 5 }}
              >
                <Typography>
                  {t('info-dialog-geographical-coverage')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ marginTop: 5 }}>
                <InfoDialogGeographicalCoverage
                  geometry={featureInfo.geometry}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {((!!featureInfo.time?.interval &&
            featureInfo.time.interval.length > 0) ||
            !!featureInfo.time?.resolution) && (
            <Accordion
              defaultExpanded={true}
              disableGutters
              onClick={(e) => e.stopPropagation()}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                style={{ paddingTop: 5, paddingBottom: 5 }}
              >
                <Typography>{t('info-dialog-time-information')}</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ marginTop: 5 }}>
                <InfoDialogTime {...featureInfo.time} />
              </AccordionDetails>
            </Accordion>
          )}

          {(!!featureInfo.properties.themes?.length ||
            !!featureInfo.properties.keywords?.length) && (
            <Accordion
              defaultExpanded={true}
              disableGutters
              onClick={(e) => e.stopPropagation()}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                style={{ paddingTop: 5, paddingBottom: 5 }}
              >
                <Typography>{t('info-dialog-theme-and-keywords')}</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ marginTop: 5 }}>
                <InfoDialogKeywords
                  keywords={featureInfo.properties.keywords}
                  themes={featureInfo.properties.themes}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {usefulLinks.length > 0 && (
            <Accordion
              defaultExpanded={true}
              disableGutters
              onClick={(e) => e.stopPropagation()}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                style={{ paddingTop: 5, paddingBottom: 5 }}
              >
                <Typography>{t('info-dialog-useful-links')}</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ marginTop: 5 }}>
                {usefulLinks.map((link) => (
                  <InfoDialogLinks key={link.href} {...link} />
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {!!featureInfo.properties.contacts?.length && (
            <Accordion
              defaultExpanded={true}
              disableGutters
              onClick={(e) => e.stopPropagation()}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                style={{ paddingTop: 5, paddingBottom: 5 }}
              >
                <Typography>{t('info-dialog-contacts')}</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ marginTop: 5 }}>
                <InfoDialogContacts {...featureInfo.properties.contacts[0]} />
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      ) : (
        <Box
          data-testid="loadingIndicator"
          onKeyDown={(event): void => {
            event.stopPropagation();
          }}
          ref={stopWheelEventPropagationRef}
        >
          <Typography>{placeHolderLoading}</Typography>
        </Box>
      )}
    </ToolContainerDraggable>
  );
};

export default DataExplorerInfo;
